import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { finalize } from 'rxjs';
import {
    FormGroup,
    FormBuilder,
    ReactiveFormsModule,
    Validators
} from '@angular/forms';
import {
    MatDialog,
    MatDialogRef,
    MAT_DIALOG_DATA,
    MatDialogModule
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PlantsService } from '../../plants/services/plants.service';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatOptionModule } from '@angular/material/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MapDialogComponent, MapResult } from '../../plants/add-plant/map-dialog/map-dialog.component';

export type NewCityDto = {
    title: string;
    timeZoneId: string;
    latitude: number;
    longitude: number;
};

export type CityOptionMoldal = {
    id: number;
    title: string;
    timeZoneId: string;
    latitude: number;
    longitude: number;
};

@Component({
    selector: 'app-add-city',
    imports: [
        CommonModule,
        MatDialogModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatAutocompleteModule,
        MatProgressSpinnerModule,
        MatOptionModule,
        MatSelectModule,
        MatIconModule,
        TranslatePipe
    ],
    standalone: true,
    templateUrl: './add-city.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './add-city.component.scss'
})
export class AddCityComponent {
    form: FormGroup = this.fb.group({
        title: ['', [Validators.required, Validators.pattern(/\S/)]],
        timeZoneId: ['', Validators.required],
        latitude: [null, [Validators.required, Validators.min(-90), Validators.max(90)]],
        longitude: [null, [Validators.required, Validators.min(-180), Validators.max(180)]]
    });

    loading = false;
    timeZones: string[] = [];
    selectedAddress: string | null = null;

    constructor(
        private fb: FormBuilder,
        private plantsSvc: PlantsService,
        private dialog: MatDialog,
        private ref: MatDialogRef<AddCityComponent, CityOptionMoldal>,
        private sb: MatSnackBar,
        private translate: TranslateService,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {}

    ngOnInit() {
        this.plantsSvc.getTimeZones().subscribe({
            next: res => {
                this.timeZones = (res?.timeZones ?? [])
                    .map((z: any) => z.title)
                    .sort();
            },
            error: () => {
                this.timeZones = [];
            }
        });
    }

    close() {
        this.ref.close();
    }

    openMap(): void {
        if (this.loading) return;

        const latitude = this.form.controls['latitude'].value;
        const longitude = this.form.controls['longitude'].value;
        const initial = this.hasValidCoordinates(latitude, longitude)
            ? {
                lat: Number(latitude),
                lng: Number(longitude),
                displayName: this.selectedAddress ?? undefined
            }
            : undefined;

        const mapRef = this.dialog.open(MapDialogComponent, {
            width: '920px',
            maxWidth: '96vw',
            maxHeight: '94vh',
            autoFocus: false,
            restoreFocus: true,
            data: { initial }
        });

        mapRef.afterClosed().subscribe((result: MapResult | undefined) => {
            if (!result) return;

            this.form.patchValue({
                latitude: this.roundCoordinate(result.lat),
                longitude: this.roundCoordinate(result.lng)
            });

            this.selectedAddress = result.displayName;
            this.form.controls['latitude'].markAsDirty();
            this.form.controls['longitude'].markAsDirty();
            this.form.controls['latitude'].updateValueAndValidity();
            this.form.controls['longitude'].updateValueAndValidity();
            this.form.updateValueAndValidity();
        });
    }

    clearSelectedAddress(): void {
        this.selectedAddress = null;
    }

    private hasValidCoordinates(latitude: unknown, longitude: unknown): boolean {
        const lat = Number(latitude);
        const lng = Number(longitude);

        return Number.isFinite(lat) && Number.isFinite(lng)
            && lat >= -90 && lat <= 90
            && lng >= -180 && lng <= 180;
    }

    private roundCoordinate(value: number): number {
        return Number(value.toFixed(7));
    }

    submit() {
        if (this.loading) return;

        // Recalculate the form status before validating. This is especially
        // important after coordinates are patched from the map dialog.
        this.form.updateValueAndValidity();

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const raw = this.form.getRawValue() as NewCityDto;
        const title = (raw.title ?? '').trim();
        const timeZoneId = (raw.timeZoneId ?? '').trim();
        const latitude = Number(raw.latitude);
        const longitude = Number(raw.longitude);

        this.loading = true;

        this.plantsSvc
            .addNewCity(title, timeZoneId, latitude, longitude)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe({
                next: (city: any) => {
                    this.notify(this.translate.instant('City added successfully'), 'success');
                    this.ref.close({
                        id: Number(city?.id ?? city?.cityId ?? -1),
                        title,
                        timeZoneId,
                        latitude: Number(city?.latitude ?? latitude),
                        longitude: Number(city?.longitude ?? longitude)
                    } as CityOptionMoldal);
                },
                error: () => {
                    this.notify(this.translate.instant('City registration failed.'), 'error');
                }
            });
    }

    getLatitudeError(): string {
        const control = this.form.controls['latitude'];
        if (control.hasError('required')) return 'Latitude is required';
        if (control.hasError('min') || control.hasError('max')) {
            return 'Latitude must be between -90 and 90';
        }
        return '';
    }

    getLongitudeError(): string {
        const control = this.form.controls['longitude'];
        if (control.hasError('required')) return 'Longitude is required';
        if (control.hasError('min') || control.hasError('max')) {
            return 'Longitude must be between -180 and 180';
        }
        return '';
    }

    private notify(
        msg: string,
        type: 'success' | 'error' | 'warn' | 'info' = 'info',
        duration = 3500
    ) {
        const cls =
            type === 'success'
                ? 'snack-success'
                : type === 'error'
                ? 'snack-error'
                : type === 'warn'
                ? 'snack-warn'
                : 'snack-info';

        this.sb.open(msg, this.translate.instant('close'), {
            duration,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: [cls]
        });
    }
}
